import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaymentMethod, PriceList, StockRow } from '@teo/shared';
import { api } from '../api/client';
import { addToCart, CartLines, cartTotal, PayGrid, paidTotal, paymentsFrom, StockSearch, type CartLine, type PayAmounts } from '../components/Operation';
import { useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatMoney } from '../lib/format';
import { Icon } from '../components/Icon';
import type { Language } from '../i18n';

const priceFor = (row: StockRow, list: PriceList): number =>
  (list === 'wholesale' ? row.wholesale_price : list === 'promo' ? row.promo_price : list === 'internal' ? row.internal_price : row.retail_price) ?? 0;

/** Venta con precio negociable y pagos mixtos: POS del local (canal store) y venta directa de Bodega (canal direct). */
export function SalePanel({
  idPrefix, channel, locationId, methods, header, onSold,
}: {
  idPrefix: 'pos' | 'direct';
  channel: 'store' | 'direct';
  locationId: number | null;
  methods: PaymentMethod[];
  header: React.ReactNode;
  onSold: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const [list, setList] = useState<PriceList>(channel === 'direct' ? 'wholesale' : 'retail');
  const [lines, setLines] = useState<CartLine[]>([]);
  const [amounts, setAmounts] = useState<PayAmounts>({});
  const [customer, setCustomer] = useState('');
  const [searchKey, setSearchKey] = useState(0);
  const [lastSale, setLastSale] = useState<{ doc: string; total: number } | null>(null);
  const scope = `venta:${channel}:${locationId}`;
  const total = cartTotal(lines);
  const ready = lines.length > 0 && Math.round(total - paidTotal(amounts)) === 0 && !!locationId;

  const clear = () => {
    setLines([]);
    setAmounts({});
    setCustomer('');
  };

  const checkout = () =>
    run(async () => {
      if (!locationId) return;
      try {
        const r = await api.createSale(requestKey(scope), {
          location_id: locationId, channel, price_list: list,
          lines: lines.map((l) => ({ variant_id: l.variant_id, qty: l.qty, unit_price: l.unit_price })),
          payments: paymentsFrom(amounts),
          customer: customer.trim() ? { name: customer.trim() } : null,
        });
        releaseKey(scope);
        clear();
        setSearchKey((k) => k + 1);
        setLastSale({ doc: r.doc_no, total: r.total ?? total });
        show(t('ops.saleDone', { doc: r.doc_no, total: formatMoney(r.total ?? total, lang) }));
        onSold();
      } catch (e) {
        showError(e);
      }
    });

  return (
    <div className="pos">
      <div className="scan">
        {header}
        {channel === 'direct' ? (
          <div style={{ marginBottom: 8 }}>
            <select className="select" id="directList" style={{ width: '100%' }} value={list} onChange={(e) => { setList(e.target.value as PriceList); setLines([]); }}>
              <option value="wholesale">{t('ops.listWholesale')}</option>
              <option value="retail">{t('ops.listRetail')}</option>
            </select>
          </div>
        ) : null}
        <StockSearch key={searchKey} idPrefix={idPrefix} locationId={locationId} placeholder={t('ops.searchPlaceholder')} onPick={(row) => { setLastSale(null); setLines((ls) => addToCart(ls, row, priceFor(row, list))); }} />
      </div>
      <div className="panel">
        <div className="head">
          <div>
            <h3>{t(channel === 'direct' ? 'ops.directSaleTitle' : 'ops.currentSale')}</h3>
            <p>{t('ops.currentSaleHelp')}</p>
          </div>
          <button type="button" className="btn sm" id={`${idPrefix}Clear`} onClick={clear}>
            {t('ops.clear')}
          </button>
        </div>
        {lastSale ? (
          <div className="salesuccess" id={`${idPrefix}Success`} role="status">
            <span className="successicon">
              <Icon name="check" size={22} />
            </span>
            <div>
              <b>{t('ops.successTitle')}</b>
              <small>{t('ops.successDetail', { doc: lastSale.doc, total: formatMoney(lastSale.total, lang) })}</small>
            </div>
            <button type="button" className="btn sm" id={`${idPrefix}NewSale`} onClick={() => setLastSale(null)}>
              {t('ops.newSale')}
            </button>
          </div>
        ) : null}
        <CartLines idPrefix={idPrefix} lines={lines} onChange={setLines} editablePrice priceLabel={t('ops.agreedPrice')} />
        <div className="total">
          <div className="totalrow">
            <span>{t('ops.totalAgreed')}</span>
            <strong id={`${idPrefix}Total`}>{formatMoney(total, lang)}</strong>
          </div>
          <PayGrid idPrefix={idPrefix} methods={methods} total={total} amounts={amounts} onChange={setAmounts} customer={customer} onCustomer={setCustomer} />
          <button type="button" className="btn green" id={`${idPrefix}Checkout`} disabled={!ready || busy} onClick={() => void checkout()}>
            {t(channel === 'direct' ? 'ops.checkoutDirect' : 'ops.checkoutStore')}
          </button>
        </div>
      </div>
    </div>
  );
}
